import {
  checkName,
  TNameValidationResponse,
  useCompanyData,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { InfoToolTip, ProceedIcon } from '@commenda/icons';
import {
  Button,
  FieldWrapper,
  Input,
  InputErrorMessage,
  Label,
  LoadingIndicator,
  PhoneInput,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SingleFileUploader,
  Textarea,
  Tooltip,
  useToggle,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { industryOptions, TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { classNames, fetcher, OptionType } from '@commenda/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CorporationType,
  Country,
  IncorporationRequestStatus,
  LegacyDocumentType,
} from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { KeyedMutator } from 'swr';
import RadioCardKS from '../../../../components/RadioCardKS';
import { getFileWithType } from '../../../common/helpers';
import {
  CompanyNameInput,
  FormInputWrapper,
  IncorporationFormHeading,
} from '../../common';
import { NameCheckerModal } from '../../common/NameCheckerModal';
import { checkCompanyEmailUniqueness } from '../../../common/helpers';
import { CompanyInfoFormFields, companyInfoSchema } from './schema';

const CompanyInfo = ({
  next,
  data,
  mutate,
  token,
  setNotification,
  companyId,
}: {
  next: () => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  mutate: KeyedMutator<unknown>;
  token: string;
  setNotification: TSetNotification;
  companyId: number;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  // Get corporations data for cross-entity email validation
  const { corporations } = useCompanyData(companyId);

  const companyNameInputRef = useRef<HTMLDivElement>();

  const nameCheckerLoadingToggle = useToggle(false);
  const nameCheckerModalVisibility = useToggle(false);
  const [nameCheckResults, setNameCheckResults] = useState<
    TNameValidationResponse[]
  >([]);

  const industrySelectOptions = industryOptions.map((each) => {
    return {
      value: each,
      text: each,
    };
  });

  const [error, setError] = useState<string | null>(null);

  const [rentalAgreement, setRenalAgreement] = useState(null);

  const currentRentalAgreement = getFileWithType(
    data?.files || [],
    LegacyDocumentType.RentalAgreement
  );

  const currentCompanyAddressProof = getFileWithType(
    data?.files || [],
    LegacyDocumentType.AddressProof
  );

  const [billForRegisteredAddress, setBillForRegisteredAddress] = useState(
    currentCompanyAddressProof || null
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    getValues,
    clearErrors,
  } = useForm<CompanyInfoFormFields>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      corporationName: data?.requestData?.companyLegalName,
      companyLegalName2: data?.requestData?.companyLegalName2 || '',
      companyLegalName3: data?.requestData?.companyLegalName3 || '',
      contactEmail: data?.requestData?.contactEmail,
      contactPhoneNumber: data?.requestData?.contactPhoneNumber || '',
      isOnRentedProperty: data?.requestData?.isOnRentedProperty || false,
      businessDescription: data?.requestData?.businessDescription,
      industry: data?.requestData?.industry,
      customIndustry: data?.requestData?.customIndustry,
      companyAddressProof: {
        id: currentCompanyAddressProof?.id || -1,
        documentType: currentCompanyAddressProof?.documentType,
        fileName: currentCompanyAddressProof?.fileName,
        fileUrl: currentCompanyAddressProof?.fileUrl,
      },
      rentalAgreement: {
        id: currentRentalAgreement?.id || -1,
        documentType: currentRentalAgreement?.documentType,
        fileName: currentRentalAgreement?.fileName,
        fileUrl: currentRentalAgreement?.fileUrl,
      },
      isVirtualAddress: data?.requestData?.isVirtualAddress || false,
    },
  });

  const handleIndustryChange = (val: string) => {
    setValue('industry', val);
    if (val !== 'Other') setValue('customIndustry', '');
  };

  const saveForm = async (formData: CompanyInfoFormFields) => {
    nameCheckerModalVisibility.hide();
    
    // Validate cross-entity email uniqueness for Indian corporations
    if (formData.contactEmail && corporations?.length) {
      const emailValidation = checkCompanyEmailUniqueness(
        formData.contactEmail,
        data?.corporationId || -1, // Use -1 for new entities (will never match existing IDs)
        corporations
      );
      
      if (!emailValidation.isUnique) {
        setNotification({
          show: true,
          valid: false,
          message: `This company email is already used by another entity. Each entity must have a unique email address.`,
          title: 'Duplicate Company Email Error',
        });
        return;
      }
    }

    const filesQuery = [];

    if (!formData.isVirtualAddress) {
      filesQuery.push(getValues('companyAddressProof'));
      if (rentalAgreement && watch('isOnRentedProperty'))
        filesQuery.push(getValues('rentalAgreement'));
    }

    const res = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          requestData: {
            companyLegalName: formData.corporationName,
            companyLegalName2: formData.companyLegalName2,
            companyLegalName3: formData.companyLegalName3,
            industry: formData.industry,
            customIndustry: formData.customIndustry,
            businessDescription: formData.businessDescription,
            contactEmail: formData.contactEmail,
            contactPhoneNumber: formData.contactPhoneNumber,
            isOnRentedProperty: formData.isOnRentedProperty,
            isVirtualAddress: formData.isVirtualAddress,
          },
          files: filesQuery,
          status: IncorporationRequestStatus.InProgress,
        },
      },
      token
    );

    if (!res.ok) {
      setNotification({
        message:
          'Something went wrong, please try again later or contact Team Commenda if you need help',
        show: true,
        valid: false,
        title: 'Error',
      });
      return;
    }

    await mutate();

    next();
  };

  const hasNameChanged = () => {
    return (
      data?.requestData?.companyLegalName !== getValues('corporationName') ||
      data?.requestData?.companyLegalName2 !== getValues('companyLegalName2') ||
      data?.requestData?.companyLegalName3 !== getValues('companyLegalName3')
    );
  };

  const onSubmit = async (formData: CompanyInfoFormFields) => {
    if (data?.corporationId && !hasNameChanged()) {
      await saveForm(formData);
      return;
    }

    if (
      formData.corporationName === formData.companyLegalName2 ||
      formData.corporationName === formData.companyLegalName3 ||
      formData.companyLegalName2 === formData.companyLegalName3
    ) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Duplicate company names are not allowed. Please enter a unique company names.',
      });
      return;
    }

    const nameCheckResponses: TNameValidationResponse[] = [];
    nameCheckerLoadingToggle.on();
    nameCheckerModalVisibility.show();

    const [nameCheckRes1, nameCheckRes2, nameCheckRes3] = await Promise.all([
      checkName({
        name: `${formData.corporationName} Private Limited`,
        country: Country.IN,
        context: {
          industry: formData.industry,
          entityType: CorporationType.PvtLtd,
          companyDescription: formData.businessDescription,
        },
      }),
      checkName({
        name: `${formData.companyLegalName2} Private Limited`,
        country: Country.IN,
        context: {
          industry: formData.industry,
          entityType: CorporationType.PvtLtd,
          companyDescription: formData.businessDescription,
        },
      }),
      checkName({
        name: `${formData.companyLegalName3} Private Limited`,
        country: Country.IN,
        context: {
          industry: formData.industry,
          entityType: CorporationType.PvtLtd,
          companyDescription: formData.businessDescription,
        },
      }),
    ]);

    if (nameCheckRes1.success) nameCheckResponses.push(nameCheckRes1.data);
    if (nameCheckRes2.success) nameCheckResponses.push(nameCheckRes2.data);
    if (nameCheckRes3.success) nameCheckResponses.push(nameCheckRes3.data);

    setNameCheckResults(nameCheckResponses);
    nameCheckerLoadingToggle.off();
  };

  const handleErrorNotifications = (formError: any) => {
    console.error(formError);
    let message = 'Please fix the highlighted fields.';
    if (formError?.companyAddressProof) {
      message = 'Please upload utility bill for registered address.';
    } else if (formError?.rentalAgreement) {
      message = 'Please upload rental agreement.';
    }
    setNotification({
      show: true,
      valid: false,
      title: 'Error',
      message,
    });
  };

  useEffect(() => {
    if (billForRegisteredAddress) {
      setValue('companyAddressProof', {
        id: currentCompanyAddressProof?.id || -1,
        documentType: LegacyDocumentType.AddressProof,
        fileName: billForRegisteredAddress.fileName,
        fileUrl: billForRegisteredAddress.fileUrl,
      });
    } else {
      setValue('companyAddressProof', null);
    }
    if (rentalAgreement) {
      setValue('rentalAgreement', {
        ...rentalAgreement,
        id: currentRentalAgreement?.id,
      });
    } else {
      setValue('rentalAgreement', null);
    }
  }, [rentalAgreement, billForRegisteredAddress]);

  useEffect(() => {
    if (!data) return;

    const currentBillForRegisteredAddress = getFileWithType(
      data?.files || [],
      LegacyDocumentType.AddressProof
    );

    if (currentBillForRegisteredAddress) {
      setValue('companyAddressProof', {
        id: currentBillForRegisteredAddress.id || -1,
        documentType: currentBillForRegisteredAddress.documentType,
        fileName: currentBillForRegisteredAddress.fileName,
        fileUrl: currentBillForRegisteredAddress.fileUrl,
      });
    }

    if (currentRentalAgreement) {
      const updateRental = {
        id: currentRentalAgreement.id || -1,
        documentType: currentRentalAgreement.documentType,
        fileName: currentRentalAgreement.fileName,
        fileUrl: currentRentalAgreement.fileUrl,
      };
      setValue('rentalAgreement', updateRental);
      setRenalAgreement(updateRental);
    }
  }, [data]);

  return (
    <form
      className="flex flex-col gap-10 max-w-[500px]"
      onSubmit={handleSubmit(onSubmit, handleErrorNotifications)}
    >
      <IncorporationFormHeading
        text="Add Company info"
        additionalText="Please provide information about this company."
      />

      <FormInputWrapper id="industry" text="Industry Type">
        <Select onValueChange={handleIndustryChange}>
          <SelectTrigger
            error={
              !watch('industry') && errors?.industry?.message
                ? errors?.industry?.message
                : null
            }
          >
            <SelectValue placeholder={watch('industry') || 'Select Industry'} />
          </SelectTrigger>
          <SelectContent>
            {industrySelectOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.text}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {watch('industry') === 'Other' && (
          <Input
            {...register('customIndustry')}
            error={errors.customIndustry?.message}
            placeholder="Enter Industry"
            required
          />
        )}
        {!watch('industry') && errors?.industry?.message ? (
          <InputErrorMessage
            className="-mt-2"
            message={errors?.industry?.message}
          />
        ) : null}
      </FormInputWrapper>

      <FormInputWrapper
        id="businessDescription"
        text="Business Description"
        description="Briefly describe the main activities or the purpose of your company in 2-3 sentences. This will help in classifying your business correctly."
      >
        <Textarea
          className="py-3 px-[14px]"
          {...register('businessDescription')}
          value={watch('businessDescription')}
          error={errors.businessDescription?.message}
          placeholder="Write a description"
        />
      </FormInputWrapper>

      <FieldWrapper ref={companyNameInputRef} _className="gap-6">
        <div className="flex flex-col gap-2">
          <Label
            htmlFor="PvtLtdName"
            className="text-base font-bold h-[22px] text-neutral-1 leading-[22px]"
          >
            Name of the Pvt. Ltd.
          </Label>
          <span className="body-15-regular text-neutral-2 leading-[22px]">
            We’ll check with the state to see if the name is available.
          </span>
        </div>
        <div>
          <CompanyNameInput
            label="Option 1"
            onValueChange={(val) => {
              setValue('corporationName', val);
              clearErrors('corporationName');
            }}
            error={errors?.corporationName?.message}
            nameValidationOptions={{
              country: Country.IN,
              companyDescription: watch('businessDescription'),
              entityType: CorporationType.LLP,
              industry: watch('industry'),
            }}
            value={watch('corporationName')}
            placeholder="Entity name 1"
          />
        </div>
        <div>
          <CompanyNameInput
            label="Option 2"
            onValueChange={(val) => {
              setValue('companyLegalName2', val);
              clearErrors('companyLegalName2');
            }}
            error={errors?.companyLegalName2?.message}
            nameValidationOptions={{
              country: Country.IN,
              companyDescription: watch('businessDescription'),
              entityType: CorporationType.LLP,
              industry: watch('industry'),
            }}
            value={watch('companyLegalName2')}
            placeholder="Entity name 2"
          />
        </div>
        <div>
          <CompanyNameInput
            label="Option 3"
            onValueChange={(val) => {
              setValue('companyLegalName3', val);
              clearErrors('companyLegalName3');
            }}
            error={errors?.companyLegalName3?.message}
            nameValidationOptions={{
              country: Country.IN,
              companyDescription: watch('businessDescription'),
              entityType: CorporationType.LLP,
              industry: watch('industry'),
            }}
            value={watch('companyLegalName3')}
            placeholder="Entity name 3"
          />
        </div>
      </FieldWrapper>

      <FormInputWrapper
        id="email"
        text="Company Email"
        toolTip={
          <Tooltip
            side="bottom"
            content="Company email must be unique across all entities and different from director emails."
          >
            <InfoToolTip />
          </Tooltip>
        }
      >
        <Input
          type="email"
          placeholder="Email"
          {...register('contactEmail')}
          error={errors?.contactEmail?.message}
        />
      </FormInputWrapper>

      <FormInputWrapper
        id="phoneNumber"
        text="Phone number"
        description="It should be different from the designated partners."
      >
        <PhoneInput
          id="phoneNumber"
          className="h-[42px]"
          {...register('contactPhoneNumber')}
          onChange={(e) => {
            setValue('contactPhoneNumber', e);
          }}
          error={errors?.contactPhoneNumber?.message}
          value={watch('contactPhoneNumber')}
          onBlur={() =>
            watch('contactPhoneNumber')
              ? clearErrors('contactPhoneNumber')
              : null
          }
        />
      </FormInputWrapper>

      <FormInputWrapper
        id="isVirtualAddress"
        text="Do you want to use Commenda's Virtual Address Service"
        description="If you choose to use Commenda's Virtual Address Service, we will provide you with a virtual address for your company and you will not be required to upload a rental agreement."
        isRequired
      >
        <div className="flex flex-col gap-2">
          <RadioGroup
            value={watch('isVirtualAddress') ? OptionType.YES : OptionType.NO}
            className="flex flex-col gap-4"
            onValueChange={(val) => {
              const isVirtual = val === OptionType.YES;
              setValue('isVirtualAddress', isVirtual);
              if (isVirtual) {
                setValue('rentalAgreement', null);
                setValue('companyAddressProof', null);
                clearErrors('rentalAgreement');
                clearErrors('companyAddressProof');
              } else {
                clearErrors('rentalAgreement');
                clearErrors('companyAddressProof');
              }
            }}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem size="lg" value={OptionType.YES} id="virtual" />
              <label htmlFor="virtual">Yes</label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem size="lg" value={OptionType.NO} id="physical" />
              <label htmlFor="physical">No</label>
            </div>
          </RadioGroup>
        </div>
      </FormInputWrapper>

      {!watch('isVirtualAddress') && (
        <>
          <FormInputWrapper
            text="Proof of address"
            id="billForRegisteredAddress"
            description="Please upload a recent utility bill (dated within the last two months) as proof of your address."
            toolTip={
              <Tooltip
                side="right"
                content="Utility bill can be electricity bill, telephone bill, internet bill, mobile bill, rental agreement, gas bill, bank statement."
              >
                <InfoToolTip />
              </Tooltip>
            }
          >
            <SingleFileUploader
              companyId={data?.companyId}
              setFile={setBillForRegisteredAddress}
              file={
                watch('companyAddressProof')?.fileUrl
                  ? (watch('companyAddressProof') as FileBasic)
                  : null
              }
              error={error}
              setError={setError}
              token={token}
              accept={{
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
              }}
              maxSize={10 * 1024 * 1024} // 10MB
              documentType={LegacyDocumentType.AddressProof}
              _className="w-full"
            />
          </FormInputWrapper>

          <FormInputWrapper
            id="isOnRentedProperty"
            text="Is this property rented or leased?"
          >
            <RadioCardKS
              heading="Yes"
              onClick={() => setValue('isOnRentedProperty', true)}
              isActive={watch('isOnRentedProperty')}
              iconClassname="size-4"
            />
            <RadioCardKS
              heading="No"
              onClick={() => setValue('isOnRentedProperty', false)}
              isActive={!watch('isOnRentedProperty')}
              iconClassname="size-4"
            />
          </FormInputWrapper>

          {watch('isOnRentedProperty') && (
            <FormInputWrapper
              text="Rental agreement"
              id="rentalAgreement"
              description="If your entity is leasing office space, please upload the rental agreement here."
            >
              <SingleFileUploader
                companyId={data?.companyId}
                setFile={setRenalAgreement}
                file={
                  watch('rentalAgreement')?.fileUrl
                    ? (watch('rentalAgreement') as FileBasic)
                    : null
                }
                error={error}
                setError={setError}
                token={token}
                accept={{
                  'application/pdf': ['.pdf'],
                  'image/*': ['.png', '.jpg', '.jpeg'],
                }}
                maxSize={10 * 1024 * 1024}
                documentType={LegacyDocumentType.RentalAgreement}
                _className="w-full"
              />
            </FormInputWrapper>
          )}
        </>
      )}

      <Button className="w-fit" disabled={isSubmitting} type="submit">
        {isSubmitting ? <LoadingIndicator size={16} /> : null}
        <span className={classNames(isSubmitting ? 'ml-2' : '')}>Continue</span>
        {!isSubmitting ? <ProceedIcon className="ml-2 h-4 w-4" /> : null}
      </Button>
      <NameCheckerModal
        isOpen={nameCheckerModalVisibility.visible}
        onClose={() => {
          nameCheckerModalVisibility.hide();
        }}
        isLoading={nameCheckerLoadingToggle.value}
        results={nameCheckResults}
        onContinue={handleSubmit(saveForm)}
      />
    </form>
  );
};

export default CompanyInfo;
